// RAMPE — panoul de depanare al căutării de rampe (modulul `architecture/rampe`).
//
// Arată, pe soluția aleasă: harta pozițiilor încercate cu costul lor, graful
// aleilor pe care se socotește accesul (alei + contactele dintre ele, cu punctul
// fiecărui contact) și seturile încercate, pe care se poate da click ca să se
// vadă cum ar fi arătat. Desenul stă peste planul soluției (excavație, clădiri,
// alei, locuri), scos din document.
//
// CADRUL e cel al viewer-ului, nu unul propriu: documentele georeferențiate se
// aduc „nord sus" cu un SFERT DE TURĂ pe grilele northing-first (Stereo 70) și
// doar cu răsturnarea lui y pe celelalte (v. `viewer/canonical.northUp`). Cu o
// simplă răsturnare, desenul ieșea oglindit față de plan.
import { useMemo, useState } from "react";
import { northingFirst } from "../viewer/canonical";

type Punct = [number, number];
type Pozitie = { x: number; y: number; deg: number; tip: string; familie: string; sub: number; cost: number };
type Alee = { i: number; a: number[]; b: number[]; w: number };
type Contact = { a: number; b: number; punct: number[]; viu: boolean | null };
type Bucata = { alee: number; de_la: number; pana_la: number };
type Referinta = { alei: { x1: number; y1: number; x2: number; y2: number; w: number }[];
                   locuri: number[][][]; pierdute: number[][] };
type Rampa = {
  type?: string; poly?: number[][]; mouth?: number[][]; apron?: number[][]; axis?: number[][];
  two_way?: boolean; slope?: number; run_m?: number; d_out?: number | null; manevra?: string | null;
  familie?: string | null;
  /** la rampa cotită la 90°: polilinia axului (tangentă, arc, drept) */
  geometry?: string; axis_path?: number[][];
};
type RampaNivel = { type?: string; poly_1?: number[][]; poly_2?: number[][]; top?: number[]; bottom?: number[];
                    geometry?: string; axis_path?: number[][] };
type Set = {
  cost: number; pierdute: number[]; accese: string[]; continuari: string[];
  continuari_nume?: string[]; rampe_acces: Rampa[]; rampe_nivel: RampaNivel[];
  /** departajarea la cost egal: distanța șanțului deschis la cea mai apropiată clădire */
  dist_fatade_m?: number;
  /** indicii locurilor pierdute de ACEASTĂ încercare, pe nivel */
  pierdute_idx?: number[][];
};
type Optiune = Set & { id: string; ramase: number[]; laturi: string[] };
export type RaportRampe = {
  status?: string; motiv?: string | null; mod?: string; cost?: number | null; clasa?: string;
  accese?: string[] | null; niveluri?: number; locuri_referinta?: number; t_s?: number;
  optim_dovedit?: boolean;
  /** ce a mai câștigat solverul de parcări re-așezând rândurile lângă rampe */
  reasezare?: string[] | null;
  referinta?: { componente?: number; locuri_fara_alee?: number };
  optiuni?: Optiune[];
  /** care opțiune s-a aplicat (implicit 0; alta când a ales-o planul de situație) */
  aleasa?: number;
  /** ce a răspuns planul de situație pe grupele de guri trimise (v. `Circulatie.tsx`) */
  /** ce a ales planul pentru subsolul ăsta, din combinația comună (`diagnostics.rampe_comune`) */
  alegerea_planului?: { intrebat: boolean; aleasa: number; motiv: string; combinatie?: string | null } | null;
  depanare?: { harta?: Pozitie[]; graf?: { alei?: Alee[]; contacte?: Contact[]; bucati_vii?: Bucata[] };
               incercari?: Set[]; pozitii_incercate?: number; referinta_plan?: Referinta };
};
type Doc = { crs?: { epsg?: number } | null; objects?: any[]; [k: string]: unknown };

const CULORI = ["#1b5e20", "#558b2f", "#f9a825", "#ef6c00", "#c62828"];
const culoare = (cost: number, maxim: number) =>
  CULORI[Math.min(CULORI.length - 1, Math.floor((cost / Math.max(1, maxim)) * CULORI.length))];

/** Aceeași aducere „nord sus" ca în viewer: sfert de tură sau răsturnarea lui y. */
function transformator(crs?: { epsg?: number } | null): (p: number[]) => Punct {
  return northingFirst(crs ?? undefined)
    ? (p) => [p[1], -p[0]]
    : (p) => [p[0], -p[1]];
}

/** Planul pe care se desenează: excavația, clădirile, aleile, locurile. */
function planDin(doc: Doc | null, T: (p: number[]) => Punct) {
  const obj = (doc?.objects ?? []) as any[];
  const prop = (o: any, k: string) => (o?.properties ?? {})[k];
  const parcare = obj.filter((o) => o.type === "Storey" && prop(o, "storey_kind") === "parking");
  const nivel1 = parcare.find((o) => prop(o, "level") === -1) ?? parcare[0];
  const peNivel = (o: any) => !nivel1 || prop(o, "storey") === nivel1.id;
  const inel = (o: any): Punct[] => ((o?.geometry?.poly ?? []) as number[][]).map(T);
  return {
    contur: obj.filter((o) => o.type === "Area" && prop(o, "kind") === "basement").map(inel),
    cladiri: obj.filter((o) => o.type === "Building").map(inel),
    alei: obj.filter((o) => o.type === "Lane" && peNivel(o))
             .map((o) => ({ a: T(o.geometry.axis[0]), b: T(o.geometry.axis[1]), w: o.geometry.w ?? 6 })),
    locuri: obj.filter((o) => o.type === "ParkingSpace" && peNivel(o))
               .map((o) => ({ c: T([o.geometry.cx, o.geometry.cy]), kind: prop(o, "kind") })),
  };
}

function cadrul(puncte: Punct[][]) {
  const xs: number[] = [], ys: number[] = [];
  for (const g of puncte) for (const p of g) { xs.push(p[0]); ys.push(p[1]); }
  if (!xs.length) return null;
  const m = 6;
  const minx = Math.min(...xs) - m, maxx = Math.max(...xs) + m;
  const miny = Math.min(...ys) - m, maxy = Math.max(...ys) + m;
  return { minx, miny, w: maxx - minx, h: maxy - miny };
}

const drum = (pts: Punct[]) => pts.map((p) => `${p[0]},${p[1]}`).join(" ");

/** O rampă de acces: gura la cotă, panta, palierul de jos, sensul și panta scrise. */
function DesenRampa({ r, T, sters }: { r: Rampa; T: (p: number[]) => Punct; sters?: boolean }) {
  const poly = (r.poly ?? []).map(T), gura = (r.mouth ?? []).map(T), apron = (r.apron ?? []).map(T);
  const ax = (r.axis ?? []).map(T);
  const traseu = (r.axis_path ?? []).map(T);
  const o = sters ? 0.35 : 1;
  let sageata: Punct[] = [];
  if (ax.length === 2 || traseu.length >= 2) {
    // săgeata la capătul de jos: pe traseul cotitei, dacă îl are, altfel pe axă
    const [a, b] = traseu.length >= 2 ? traseu.slice(-2) : ax;
    const d = [b[0] - a[0], b[1] - a[1]];
    const L = Math.hypot(d[0], d[1]) || 1;
    const u: Punct = [d[0] / L, d[1] / L], n: Punct = [-u[1], u[0]];
    const v: Punct = [b[0] - u[0] * 1.2, b[1] - u[1] * 1.2];
    sageata = [[b[0], b[1]], [v[0] + n[0] * 1.1, v[1] + n[1] * 1.1], [v[0] - n[0] * 1.1, v[1] - n[1] * 1.1]];
  }
  return (
    <g opacity={o}>
      {poly.length > 0 && <polygon points={drum(poly)} fill="#e7c9dc" fillOpacity={0.75} stroke="#8e3b73" strokeWidth={0.3} />}
      {gura.length > 0 && <polygon points={drum(gura)} fill="#fff59d" stroke="#f9a825" strokeWidth={0.3} />}
      {apron.length > 0 && <polygon points={drum(apron)} fill="none" stroke="#00897b" strokeWidth={0.4} strokeDasharray="1.2 0.8" />}
      {traseu.length >= 2
        ? <polyline points={drum(traseu)} fill="none" stroke="#5e2449" strokeWidth={0.25} strokeDasharray="0.8 0.8" />
        : ax.length === 2 && <line x1={ax[0][0]} y1={ax[0][1]} x2={ax[1][0]} y2={ax[1][1]} stroke="#5e2449" strokeWidth={0.25} strokeDasharray="0.8 0.8" />}
      {sageata.length === 3 && <polygon points={drum(sageata)} fill="#5e2449" />}
      {ax.length === 2 && (
        <text x={(ax[0][0] + ax[1][0]) / 2} y={(ax[0][1] + ax[1][1]) / 2 - 1.2}
              fontSize={2.2} fill="#5e2449" textAnchor="middle">
          {`${r.type ?? ""}${r.geometry === "cotita" ? " ⤵90°" : ""} ${r.two_way ? "⇅" : "↓"} ${Math.round((r.slope ?? 0) * 100)}%`}
        </text>
      )}
      <title>
        {[`rampă de acces ${r.type ?? ""}${r.geometry === "cotita" ? " cotită la 90°" : ""}`,
          r.two_way ? "dublu sens (un fir pe sens)" : "sens unic (un fir)",
          `pantă ${Math.round((r.slope ?? 0) * 100)}%`,
          r.run_m ? `lungime ${r.run_m} m` : "",
          r.d_out != null ? `gura la ${r.d_out} m în afara excavației` : "",
          r.manevra ? `podea de manevră: ${r.manevra}` : "",
          r.familie ? `poziție din familia ${r.familie}` : ""].filter(Boolean).join(" · ")}
      </title>
    </g>
  );
}

function DesenContinuare({ c, T, sters }: { c: RampaNivel; T: (p: number[]) => Punct; sters?: boolean }) {
  const jos = (c.poly_2 ?? c.poly_1 ?? []).map(T);
  if (!jos.length) return null;
  const sus = c.top ? T(c.top) : null;
  const traseu = (c.axis_path ?? []).map(T);
  return (
    <g opacity={sters ? 0.35 : 1}>
      <polygon points={drum(jos)} fill="#ce93d8" fillOpacity={0.6} stroke="#6a1b9a" strokeWidth={0.3} />
      {traseu.length >= 2 && <polyline points={drum(traseu)} fill="none" stroke="#4a148c" strokeWidth={0.25} strokeDasharray="0.8 0.8" />}
      {sus && <text x={sus[0]} y={sus[1]} fontSize={2.2} fill="#4a148c" textAnchor="middle">{`${c.type} ↓−2`}</text>}
      <title>{`rampa −1 → −2, ${c.type}`}</title>
    </g>
  );
}

export default function RampeDepanare({ raport, doc }: { raport: RaportRampe | null; doc?: Doc | null }) {
  const [strat, setStrat] = useState<"harta" | "graf" | "incercari">("harta");
  const [aleasa, setAleasa] = useState(0);
  const [nivel, setNivel] = useState(0);        // 0 = −1, 1 = −2
  const T = useMemo(() => transformator(doc?.crs), [doc]);
  const plan = useMemo(() => planDin(doc ?? null, T), [doc, T]);
  const dep = raport?.depanare;
  const alei = dep?.graf?.alei ?? [];
  const harta = dep?.harta ?? [];
  const incercari = dep?.incercari ?? [];
  const optiune = raport?.optiuni?.[raport.aleasa ?? 0] ?? raport?.optiuni?.[0] ?? null;
  const set: Set | null = strat === "incercari" ? (incercari[aleasa] ?? null) : optiune;

  // PLANȘA DE DINAINTE DE RAMPE, când raportul o poartă: aleile întregi și
  // toate locurile, ca să se vadă de la ce s-a plecat și ce s-a pierdut.
  const ref = dep?.referinta_plan ?? null;
  // locurile pierdute se iau de la SETUL DESENAT — altfel, uitându-te la o
  // încercare, vedeai în roșu pierderile câștigătoarei și nu costul ei (Oana)
  const pierduteAcum = (set?.pierdute_idx ?? ref?.pierdute ?? [])[nivel] ?? [];
  const refDesen = useMemo(() => {
    if (!ref) return null;
    return {
      alei: ref.alei.map((a) => ({ a: T([a.x1, a.y1]), b: T([a.x2, a.y2]), w: a.w })),
      locuri: ref.locuri.map((ring) => ring.map(T)),
      pierdute: new Set(pierduteAcum),
    };
  }, [ref, T, pierduteAcum]);

  const cadru = useMemo(() => {
    const g: Punct[][] = [...plan.contur, ...plan.cladiri,
                          plan.alei.flatMap((a) => [a.a, a.b]),
                          plan.locuri.map((l) => l.c),
                          ...(refDesen?.locuri ?? []),
                          alei.flatMap((a) => [T(a.a), T(a.b)]),
                          harta.map((p) => T([p.x, p.y]))];
    return cadrul(g.filter((x) => x.length));
  }, [plan, refDesen, alei, harta, T]);

  if (!raport) return <p className="sg-muted">Documentul n-are raportul de rampe.</p>;
  const maxim = Math.max(1, ...harta.map((p) => p.cost));
  const vii = new Set((dep?.graf?.bucati_vii ?? []).map((b) => b.alee));

  return (
    <div>
      <p className="mono" style={{ fontSize: 12 }}>
        {raport.status === "ok"
          ? `${raport.cost} locuri pierdute · ${(raport.accese ?? []).join("+")} · clasa ${raport.clasa} · ${raport.niveluri} niveluri · mod ${raport.mod}${raport.optim_dovedit ? " · optim dovedit" : ""} · ${raport.t_s ?? "?"} s`
          : `infezabil: ${raport.motiv ?? "—"}`}
      </p>
      {raport.alegerea_planului && (
        <p className="sg-muted" style={{ fontSize: 12 }} title="planul de situație a ales între opțiunile de cost minim">
          planul de situație: {raport.alegerea_planului.intrebat
            ? `aplicată opțiunea o${raport.alegerea_planului.aleasa}, din combinația ${raport.alegerea_planului.combinatie ?? "?"} · ${raport.alegerea_planului.motiv}`
            : raport.alegerea_planului.motiv}
        </p>
      )}
      {(raport.reasezare ?? []).length > 0 && (
        <p className="sg-muted" style={{ fontSize: 12 }}>
          re-așezare lângă rampe: {(raport.reasezare ?? []).join(" · ")}
        </p>
      )}
      <p className="sg-muted" style={{ fontSize: 12 }}>
        {dep?.pozitii_incercate ?? 0} poziții încercate · {alei.length} alei ·{" "}
        {(dep?.graf?.contacte ?? []).length} contacte între alei · referința:{" "}
        {raport.referinta?.componente ?? "?"} componente, {raport.referinta?.locuri_fara_alee ?? "?"} locuri fără alee
      </p>
      <div className="ob-film-cap">
        {(["harta", "graf", "incercari"] as const).map((s) => (
          <button key={s} onClick={() => setStrat(s)} disabled={strat === s}>
            {s === "harta" ? "harta de cost" : s === "graf" ? "graful aleilor" : "încercări"}
          </button>
        ))}
      </div>
      <div className="ob-film-cap" style={{ marginTop: 2 }}>
        {(raport.niveluri ?? 1) > 1 && [0, 1].map((k) => (
          <button key={`n${k}`} onClick={() => setNivel(k)} disabled={nivel === k}
                  style={{ padding: "1px 6px", fontSize: 11 }}>
            {`S${-(k + 1)}`}
          </button>
        ))}
        <span className="sg-muted">
          {ref ? `planșa de dinainte: ${ref.locuri.length} locuri, din care ${pierduteAcum.length} pierdute aici`
               : "planșa de dinainte lipsește (rulare veche)"}
        </span>
      </div>
      {cadru ? (
        <svg viewBox={`${cadru.minx} ${cadru.miny} ${cadru.w} ${cadru.h}`}
             style={{ width: "100%", height: 420, background: "#fff", border: "1px solid #ddd" }}>
          {/* PLANUL: excavația, clădirile, aleile, locurile */}
          {plan.contur.map((g, i) => (
            <polygon key={`c${i}`} points={drum(g)} fill="#fafafa" stroke="#455a64" strokeWidth={0.5} />
          ))}
          {plan.cladiri.map((g, i) => (
            <polygon key={`b${i}`} points={drum(g)} fill="none" stroke="#bdbdbd" strokeWidth={0.4} strokeDasharray="2 1.5" />
          ))}
          {/* aleile: cele ÎNTREGI ale soluției de dinainte, dacă le avem */}
          {(refDesen?.alei ?? plan.alei).map((a, i) => (
            <line key={`l${i}`} x1={a.a[0]} y1={a.a[1]} x2={a.b[0]} y2={a.b[1]}
                  stroke="#d6d6d6" strokeWidth={a.w} strokeLinecap="butt" />
          ))}
          {/* locurile: toate cele de dinainte de rampe; roșu = pierdute pe nivelul ales */}
          {refDesen
            ? refDesen.locuri.map((ring, i) => (
                <polygon key={`rs${i}`} points={drum(ring)}
                         fill={refDesen.pierdute.has(i) ? "#ef9a9a" : "#e3f2fd"}
                         stroke={refDesen.pierdute.has(i) ? "#b71c1c" : "#1976d2"} strokeWidth={0.15}>
                  <title>{refDesen.pierdute.has(i) ? `loc ${i}: pierdut` : `loc ${i}`}</title>
                </polygon>
              ))
            : plan.locuri.map((l, i) => (
                <rect key={`s${i}`} x={l.c[0] - 1.1} y={l.c[1] - 1.1} width={2.2} height={2.2}
                      fill={l.kind === "ada" ? "#90caf9" : "#e3f2fd"} stroke="#1976d2" strokeWidth={0.15} />
              ))}

          {strat === "harta" && harta.map((p, i) => {
            const q = T([p.x, p.y]);
            return (
              <circle key={i} cx={q[0]} cy={q[1]} r={0.9} fill={culoare(p.cost, maxim)} opacity={0.85}>
                <title>{`${p.familie} ${p.tip} · cost ${p.cost} locuri · acoperă ${p.sub} · ${p.deg}°`}</title>
              </circle>
            );
          })}

          {strat === "graf" && alei.map((a) => {
            const [p1, p2] = [T(a.a), T(a.b)];
            const viu = vii.size === 0 || vii.has(a.i);
            return (
              <g key={`g${a.i}`}>
                <line x1={p1[0]} y1={p1[1]} x2={p2[0]} y2={p2[1]}
                      stroke={viu ? "#2e7d32" : "#c62828"} strokeWidth={1.2} strokeLinecap="round" />
                <text x={(p1[0] + p2[0]) / 2} y={(p1[1] + p2[1]) / 2 - 0.8} fontSize={2} fill="#1b5e20"
                      textAnchor="middle">{a.i}</text>
                <title>{`aleea ${a.i} · ${a.w} m`}</title>
              </g>
            );
          })}
          {strat === "graf" && (dep?.graf?.contacte ?? []).map((c, i) => {
            const q = T(c.punct);
            return (
              <circle key={`k${i}`} cx={q[0]} cy={q[1]} r={1.3}
                      fill={c.viu === false ? "#c62828" : "#6a1b9a"} fillOpacity={0.85}>
                <title>{`contact alee ${c.a} ↔ ${c.b}${c.viu === false ? " — RUPT de rampă" : ""}`}</title>
              </circle>
            );
          })}

          {/* rampele: ale soluției alese, sau ale încercării pe care ai dat click.
              Accesul se vede pe -1; corpul continuării, pe nivelul unde îl are.
              NU pe harta de cost: acolo desenul e despre punctele încercate, iar
              o rampă peste ele încurcă cititul (Oana). */}
          {strat !== "harta" && nivel === 0
            && (set?.rampe_acces ?? []).map((r, i) => <DesenRampa key={`r${i}`} r={r} T={T} />)}
          {strat !== "harta" && (set?.rampe_nivel ?? []).map((c, i) => (
            <DesenContinuare key={`n${i}`} c={nivel === 0 ? { ...c, poly_2: c.poly_1 } : c} T={T} />
          ))}
        </svg>
      ) : <p className="sg-muted">Fără date de desenat.</p>}

      <table className="mono" style={{ marginTop: 6 }}>
        <thead><tr><th>#</th><th>cost</th><th>pe nivel</th><th>fațade</th><th>accese</th><th>continuare</th></tr></thead>
        <tbody>
          {incercari.map((inc, i) => (
            <tr key={i} className={strat === "incercari" && i === aleasa ? "castigator" : undefined}
                style={{ cursor: "pointer" }}
                onClick={() => { setStrat("incercari"); setAleasa(i); }}>
              <td>{i + 1}{i === 0 ? " ★" : ""}</td>
              <td>{inc.cost}</td>
              <td>{inc.pierdute.join(" / ")}</td>
              <td>{inc.dist_fatade_m != null ? `${inc.dist_fatade_m.toFixed(1)} m` : "—"}</td>
              <td>{inc.accese.join("+")}</td>
              <td>{(inc.continuari_nume ?? inc.continuari).join(" + ") || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="sg-muted" style={{ fontSize: 12, marginTop: 4 }}>
        Desenul e în cadrul planului (nord sus). Gri = aleile soluției ÎNAINTE de rampe,
        albastru = locurile ei, roșu = locurile pierdute de setul desenat acum (câștigătoarea, sau
        încercarea pe care ai dat click), pe subsolul ales din «S−1 / S−2».
        Harta: un punct pe gura fiecărei rampe încercate, colorat după costul ei (verde = ieftin) —
        costul e întreg (locuri acoperite + locuri rămase fără acces la −1), iar rampele nu se
        desenează peste hartă. Locurile din mijlocul parcării lipsesc fiindcă acolo o gură acoperă
        mai multe locuri decât pragul până la care se evaluează exact.
        Graful: aleile ca noduri numerotate (verde = mai are bucăți vii, roșu = moartă), iar
        bulinele violet sunt contactele dintre ele — pe acolo se ocolește o rampă pusă peste o
        intersecție; roșu = contact rupt de rampele alese. Rampa: galben = gura la cotă (6 m la
        5 %), prună = panta, turcoaz punctat = podeaua de manevră de jos, violet = rampa
        spre −2. Click pe un rând din tabel desenează acea încercare.
      </p>
    </div>
  );
}
